import { compareMissingLast } from "@/profitability/missing";
import {
  AGGREGATE,
  type ReportAggregate,
  type ReportSort,
  type ReportValue,
} from "@/profitability/types";

export interface ChartPoint {
  label: string;
  value: number | null;
  sort: ReportValue | undefined;
}

/**
 * A chart's points in the order it was asked for, its long tail past the
 * declared limit summed into one Other where that adds up.
 */
export const orderedPoints = (
  points: ChartPoint[],
  sort: ReportSort,
  limit: number | undefined,
  aggregate: ReportAggregate,
) => {
  // Grouped rows arrive in the order the data held them, which is an order
  // for nothing, so every chart is put in the one it was asked for.
  const direction = sort.direction === "asc" ? 1 : -1;
  const sorted = [...points].sort((a, b) =>
    sort.by === "value"
      ? // A point without a value is missing, not zero: it sorts last.
        compareMissingLast(
          a.value,
          b.value,
          sort.direction,
          (left, right) => left - right,
        )
      : String(a.sort ?? "").localeCompare(String(b.sort ?? ""), undefined, {
          numeric: true,
        }) * direction,
  );
  // Past the declared limit a long tail becomes one Other, so the chart
  // still says where the money went without a hundred slivers.
  //
  // Only where adding the tail up means something. A sum of sums is a sum
  // and a sum of counts is a count, but a sum of averages is nothing at all,
  // and neither is a sum of minimums. Those charts just stop at the limit.
  if (!limit || sorted.length <= limit) return sorted;
  const addable = aggregate === AGGREGATE.sum || aggregate === AGGREGATE.count;
  return [
    ...sorted.slice(0, limit),
    ...(addable
      ? [
          otherPoint(
            sorted
              .slice(limit)
              .reduce((total, point) => total + (point.value ?? 0), 0),
          ),
        ]
      : []),
  ];
};

const otherPoint = (value: number): ChartPoint => ({
  label: "Other",
  sort: "Other",
  value,
});

/**
 * The points of a chart whose limit the API already applied: its top groups,
 * in the chart's order, and the rest it summed, when it did. The same
 * points orderedPoints makes from every group.
 */
export const toppedPoints = (
  points: ChartPoint[],
  sort: ReportSort,
  other: number | null | undefined,
) => [
  ...orderedPoints(points, sort, undefined, AGGREGATE.sum),
  ...(other == null ? [] : [otherPoint(other)]),
];
